import React from 'react'
import * as yup from 'yup'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Modal,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Input,
  Button,
  Textarea,
  Indicators,
  Content,
} from 'ui'
import {
  useSchoolRequestMutation,
  SchoolRequestData,
} from '../queries/useSchoolRequestMutation'

// Schema for form validation
const schoolRequestSchema = yup.object({
  district_name: yup.string().required('District name is required'),
  school_name: yup.string().required('School name is required'),
  requester_email: yup
    .string()
    .email('Must be a valid email address')
    .required('Email is required'),
  additional_notes: yup.string().optional(),
})

type SchoolRequestFormValues = yup.InferType<typeof schoolRequestSchema>

interface SchoolRequestModalProps {
  isOpen: boolean
  onClose: () => void
  defaultEmail?: string
}

export const SchoolRequestModal: React.FC<SchoolRequestModalProps> = ({
  isOpen,
  onClose,
  defaultEmail = '',
}) => {
  const {
    mutateAsync: submitSchoolRequest,
    isLoading,
  } = useSchoolRequestMutation()

  const form = useForm<SchoolRequestFormValues>({
    defaultValues: {
      district_name: '',
      school_name: '',
      requester_email: defaultEmail,
      additional_notes: '',
    },
    resolver: yupResolver(schoolRequestSchema),
  })

  const handleSubmit = async (values: SchoolRequestFormValues) => {
    try {
      await submitSchoolRequest(values as SchoolRequestData)
      form.reset()
      onClose()
    } catch (error) {
      // Error handling is done in the mutation
    }
  }

  const handleDismissDialog = () => {
    onClose()
  }

  return isOpen ? (
    <Modal.View onDismiss={handleDismissDialog} size={Modal.Size.default}>
      <div className="p-6">
        <Content.Heading className="mb-2">
          Request School Addition
        </Content.Heading>
        <Content.Body className="mb-6">
          Please provide your school and district information below. We'll
          review your request and add it to our system.
        </Content.Body>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="district_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>District Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter district name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="school_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>School Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter school name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="requester_email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Your Email</FormLabel>
                  <FormControl>
                    <Input placeholder="your@email.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="additional_notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Additional Notes (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Any additional information that might be helpful..."
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Indicators.Spinner size="sm" className="mr-2" />
                    Submitting...
                  </>
                ) : (
                  'Submit Request'
                )}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </Modal.View>
  ) : null
}
